// City tour, "follow the night": which city is deepest in the night right now, from the registry's time zones.
// Pure (no DOM, no timers), so node can test it: node --test tests/
const HEART = 60;                               // the heart of the night: 01:00 local, in minutes past midnight
const DAY = 24 * 60;

const fmt = new Map();
/** Minutes past local midnight (0–1439) in an IANA time zone. */
export function localMinutes(timeZone, date = new Date()) {
  if (!fmt.has(timeZone)) fmt.set(timeZone, new Intl.DateTimeFormat('en-GB', { timeZone, hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }));
  const parts = fmt.get(timeZone).formatToParts(date);
  const part = type => +parts.find(p => p.type === type).value;
  return (part('hour') % 24) * 60 + part('minute');
}

const fromHeart = local => { const d = Math.abs(local - HEART); return Math.min(d, DAY - d); };

/** How far local time is from 1 am, either way round the clock, in minutes (0–720). */
export function nightDistance(timeZone, date = new Date()) {
  return fromHeart(localMinutes(timeZone, date));
}

/**
 * The scene whose local time is closest to 1 am. As the hours pass the pick moves west with the night
 * (Sydney → Tokyo, Seoul → Hong Kong, Singapore → Bangkok → New Delhi → Dubai → Istanbul, Cairo → Paris, Hamburg,
 * Amsterdam, Madrid, Rome, Prague, Munich, Cape Town → London, Lisbon → Rio → New York → Mexico City → San Francisco,
 * Vancouver; in the European winter Cape Town and Cairo share an hour after Istanbul, and from November 2026
 * Vancouver leads alone in the US winter).
 * Cities on the same clock share their turn: the stretch of night they lead is split evenly between them, in registry
 * order. Ties between different clocks keep `prefer` (the city on screen), then registry order; on a shared clock the
 * pick never steps back from `prefer` to an earlier twin.
 */
export function nightCity(scenes, date = new Date(), prefer = null) {
  const utc = date.getUTCHours() * 60 + date.getUTCMinutes();
  let best = null;
  const clocks = scenes.map(s => {
    const local = localMinutes(s.timeZone, date), c = { s, local, offset: (local - utc + DAY) % DAY, d: fromHeart(local) };
    if (!best || c.d < best.d || (c.d === best.d && s.id === prefer)) best = c;
    return c;
  });
  if (!best) return null;
  const twins = clocks.filter(c => c.offset === best.offset);
  if (twins.length === 1) return best.s;
  // their lead runs from halfway to the next clock east until halfway to the next clock west
  const others = clocks.filter(c => c.offset !== best.offset);
  const gap = dir => Math.min(DAY, ...others.map(c => (dir * (c.offset - best.offset) + DAY) % DAY));
  const east = gap(1), west = gap(-1);
  const pastHeart = (best.local - HEART + DAY + DAY / 2) % DAY - DAY / 2;   // -720..719
  const i = Math.floor((pastHeart + east / 2) / ((east + west) / 2) * twins.length);
  // never back to an earlier twin than the one on screen: when the clocks fall back in the middle of a shared turn
  // (London and Lisbon at 01:00 UTC in October), local time replays an hour that the next twin has already had
  const held = twins.findIndex(c => c.s.id === prefer);
  return twins[Math.min(Math.max(i, held, 0), twins.length - 1)].s;
}
